import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PortalLayout from '../PortalLayout';
import { IcibAdminProvider, useIcibAdmin } from './context/IcibAdminContext';
import { NAV } from './nav';
import { useAuth } from '../../auth/AuthContext';
import { userLabel, userInitials } from '../../auth/roles';

// Routes that only make sense with no employer group selected — they
// manage things that aren't scoped to one group at all (every group,
// every user). Everything else, Overview ('') included, needs one group
// in focus to mean anything — Overview shows that group's roster counts
// and notification queue, same as the rest. The two sets are a strict
// partition, not "always visible plus more once a group's picked": each
// is shown only in its own scope. Kept as route paths (matching nav.js's
// `path`), since that's what both the nav filter and the route-guard
// redirect below key off.
const UNSCOPED_PATHS = ['groups', 'users'];
// Landing pages the redirect below sends the user to when their current
// route falls out of scope: Overview for "a group just got selected"
// (its natural per-group landing page), Employer groups for "back to
// All groups" (Overview no longer applies with no group selected).
const SCOPED_LANDING_PATH = '';
const UNSCOPED_LANDING_PATH = 'groups';

// Splits NAV along that partition: "All groups" shows only the unscoped
// section; a real group shows everything else (Overview included).
// Filters out any section left with no items rather than showing an
// empty section heading.
function visibleNav(hasGroup) {
  return NAV
    .map((section) => ({ ...section, items: section.items.filter((item) => UNSCOPED_PATHS.includes(item.path) !== hasGroup) }))
    .filter((section) => section.items.length);
}

function DashboardLayoutInner() {
  const { db, groupId, setGroupId, curGroup, refreshing } = useIcibAdmin();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const section = location.pathname.replace(/^\/dashboard\/?/, '');

  // Each route only exists in one scope (see UNSCOPED_PATHS above) — the
  // moment the scope selector moves the current route out of view (either
  // direction), send the user to that scope's landing page rather than
  // leaving them stranded on a now-hidden page.
  useEffect(() => {
    const isUnscoped = UNSCOPED_PATHS.includes(section);
    if (!groupId && !isUnscoped) navigate(`/dashboard/${UNSCOPED_LANDING_PATH}`);
    else if (groupId && isUnscoped) navigate(SCOPED_LANDING_PATH ? `/dashboard/${SCOPED_LANDING_PATH}` : '/dashboard');
  }, [groupId, section, navigate]);

  return (
    <PortalLayout
      navSections={visibleNav(!!groupId)}
      brandRole="Administrator console"
      userLabel={userLabel(user, 'ICIB Admin')}
      userInitials={userInitials(user)}
      loading={refreshing}
      groupScope={{
        value: groupId ?? '',
        onChange: (v) => setGroupId(v || null),
        tag: curGroup()?.company_code,
        options: [
          { id: '', label: 'All companies' },
          ...db.groups.filter((g) => g.status === 'active').map((g) => ({ id: g.id, label: `${g.name} · ${g.company_code}` })),
        ],
      }}
    />
  );
}

/* ICIB Admin Console shell — see the sample UI's #rail / #topbar / #view.
   Wraps every ICIB admin route in IcibAdminProvider so all the story
   views (ICIB-01..11) share the one mock "backend" and selected group.
   RequireRole (see src/App.js) already guarantees `user` is set and
   role === 'icib_admin' by the time this renders. */
function DashboardLayout() {
  return (
    <IcibAdminProvider>
      <DashboardLayoutInner />
    </IcibAdminProvider>
  );
}

export default DashboardLayout;
